import classNames from 'classnames'
import React, { useState } from 'react'
import Icon from '../icon'
import { IconType } from '../../../types/icon-name-types'

export interface InlineNotificationProps {
  // 'disclaimer' is a distinct variant from 'info' — the requirements doc's Figma spec
  // for this banner (a #236FA6 full-height stripe down the left edge, white icon, white
  // card, 1px #808080 inset outline) doesn't match the shared 'info' tokens, so it gets
  // its own modifier rather than overriding 'info' sitewide (NZTAJP-699, on review —
  // additive, not a replacement).
  messageType?: 'info' | 'success' | 'warning' | 'error' | 'disclaimer'
  isDismissable?: boolean
  // Called when the built-in dismiss button is used, in addition to the component's own
  // dismissed state — lets a caller (e.g. DisclaimerBanner) persist the dismissal itself.
  onDismiss?: () => void
}

export default function InlineNotification({
  messageType = 'info',
  isDismissable = false,
  onDismiss,
  children,
}: React.PropsWithChildren<InlineNotificationProps>) {
  const [isDismissed, setIsDismissed] = useState(false)
  const messageClasses = classNames('c-inline-notification', {
    [`c-inline-notification--${messageType}`]: !!messageType,
  })

  const iconOptions: { [key: string]: IconType } = {
    info: 'info',
    success: 'check-circle',
    warning: 'warning',
    error: 'warning',
    // 'info-mark' is the same glyph as 'info' with its ring outline removed — the
    // disclaimer variant draws its own white circle behind the icon, so 'info's ring
    // would double up with it (NZTAJP-699, on review).
    disclaimer: 'info-mark',
  }

  function handleDismiss() {
    setIsDismissed(true)
    onDismiss?.()
  }

  // 'cross-circle' hardcodes fill="#323232" rather than using currentColor, so it can't
  // be recoloured via CSS — irrelevant for the shared variants (which don't recolour the
  // dismiss button), but the disclaimer variant does, so it needs 'close' (a plain X, no
  // ring, and currentColor-based) instead (NZTAJP-699, on review).
  const dismissIcon = messageType === 'disclaimer' ? 'close' : 'cross-circle'

  return isDismissed ? null : (
    <div className={messageClasses} role='alert' aria-label={`${messageType} notification message`}>
      <Icon icon={iconOptions[messageType]} className='c-inline-notification__icon' ariaHidden={true} />
      <div className='c-inline-notification__message'>{children}</div>
      {isDismissable && (
        <button className='c-inline-notification__dismiss-btn' onClick={handleDismiss}>
          <Icon icon={dismissIcon} ariaLabel='Dismiss notification' />
        </button>
      )}
    </div>
  )
}
